import React from 'react';
import { X, Trash2, Plus, Minus, ShoppingBag, ArrowRight, Truck, Award } from 'lucide-react';
import { CartItem, DistributorConfig } from '../types';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  cart: CartItem[];
  distributor: DistributorConfig;
  onUpdateQuantity: (productId: string, quantity: number) => void;
  onRemoveItem: (productId: string) => void;
  onProceedToCheckout: () => void;
}

export const CartDrawer: React.FC<CartDrawerProps> = ({
  isOpen,
  onClose,
  cart,
  distributor,
  onUpdateQuantity,
  onRemoveItem,
  onProceedToCheckout,
}) => {
  if (!isOpen) return null;

  const subtotal = cart.reduce(
    (sum, item) => sum + item.product.memberPrice * item.quantity,
    0
  );
  const totalPv = cart.reduce(
    (sum, item) => sum + item.product.pv * item.quantity,
    0
  );
  const isFreeShipping = subtotal >= distributor.freeShippingThreshold;
  const neededForFreeShipping = Math.max(
    0,
    distributor.freeShippingThreshold - subtotal
  );

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/60 backdrop-blur-xs transition-opacity">
      <div className="w-full max-w-md bg-white h-full shadow-2xl flex flex-col justify-between animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="p-4 border-b border-blue-100 flex items-center justify-between bg-blue-50/50">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-blue-600" />
            <h2 className="text-base font-bold text-slate-800">
              ตะกร้าสินค้า ({cart.reduce((acc, i) => acc + i.quantity, 0)} ชิ้น)
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-blue-100/70 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Free Shipping Progress Indicator (Emerald Green #059669 / #10B981) */}
        <div className="p-3.5 bg-emerald-50/60 border-b border-emerald-100">
          <div className="flex items-center justify-between text-xs mb-1.5 font-medium">
            <span className="flex items-center gap-1.5 text-slate-700">
              <Truck className="w-4 h-4 text-emerald-600" />
              {isFreeShipping ? (
                <span className="text-emerald-700 font-bold">
                  ยินดีด้วย! คุณได้รับสิทธิ์จัดส่งฟรีแล้ว
                </span>
              ) : (
                <span>
                  สั่งซื้อเพิ่มอีก{' '}
                  <strong className="text-emerald-700 font-bold">
                    ฿{neededForFreeShipping.toLocaleString()}
                  </strong>{' '}
                  เพื่อรับสิทธิ์ส่งฟรี
                </span>
              )}
            </span>
            <span className="text-slate-500 font-mono text-[11px]">
              {Math.min(100, Math.round((subtotal / distributor.freeShippingThreshold) * 100))}%
            </span>
          </div>
          <div className="w-full bg-emerald-200/60 rounded-full h-1.5 overflow-hidden">
            <div
              className="bg-gradient-to-r from-[#10B981] to-[#059669] h-full rounded-full transition-all duration-300"
              style={{
                width: `${Math.min(
                  100,
                  (subtotal / distributor.freeShippingThreshold) * 100
                )}%`,
              }}
            />
          </div>
        </div>

        {/* Cart Item List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {cart.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-6 text-slate-400">
              <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mb-3 text-blue-300">
                <ShoppingBag className="w-8 h-8" />
              </div>
              <p className="text-sm font-semibold text-slate-700">
                ยังไม่มีสินค้าในตะกร้า
              </p>
              <p className="text-xs text-slate-400 mt-1">
                เลือกสินค้ากิฟฟารีนคุณภาพลงตะกร้าเพื่อเริ่มต้นสั่งซื้อ
              </p>
            </div>
          ) : (
            cart.map((item) => (
              <div
                key={item.product.id}
                className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-white hover:border-blue-300 transition-colors shadow-2xs"
              >
                <img
                  src={item.product.image}
                  alt={item.product.name}
                  className="w-16 h-16 rounded-lg object-cover bg-slate-50 shrink-0"
                />

                <div className="flex-1 min-w-0">
                  <h4 className="text-xs font-bold text-slate-800 truncate">
                    {item.product.name}
                  </h4>
                  <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5">
                    <span className="font-mono text-slate-400">รหัส {item.product.code}</span>
                    <span>•</span>
                    <span className="text-slate-400 font-normal">{item.product.unit}</span>
                  </div>
                  <div className="text-sm font-bold text-blue-700 mt-1">
                    ฿{(item.product.memberPrice * item.quantity).toLocaleString()}
                  </div>
                </div>

                {/* Quantity + Remove */}
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <button
                    onClick={() => onRemoveItem(item.product.id)}
                    className="text-slate-300 hover:text-rose-600 transition-colors p-1"
                    title="ลบรายการ"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>

                  <div className="flex items-center border border-slate-200 rounded-lg bg-slate-50">
                    <button
                      onClick={() =>
                        onUpdateQuantity(item.product.id, item.quantity - 1)
                      }
                      className="w-6 h-6 flex items-center justify-center text-slate-600 hover:text-blue-600 text-xs"
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="w-7 text-center text-xs font-bold text-slate-800">
                      {item.quantity}
                    </span>
                    <button
                      onClick={() =>
                        onUpdateQuantity(item.product.id, item.quantity + 1)
                      }
                      className="w-6 h-6 flex items-center justify-center text-slate-600 hover:text-blue-600 text-xs"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer Summary & Checkout */}
        {cart.length > 0 && (
          <div className="p-4 border-t border-slate-200 bg-white space-y-3">
            <div className="space-y-1.5 text-xs text-slate-600">
              <div className="flex justify-between">
                <span>ยอดรวมสินค้า ({cart.reduce((a, b) => a + b.quantity, 0)} ชิ้น):</span>
                <span className="font-semibold text-slate-800">฿{subtotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>ค่าจัดส่ง:</span>
                <span className={isFreeShipping ? 'text-emerald-600 font-bold' : 'text-slate-800'}>
                  {isFreeShipping ? 'ฟรี (โปรโมชั่น)' : `฿${distributor.shippingFee}`}
                </span>
              </div>
              <div className="pt-2 border-t border-slate-100 flex justify-between items-baseline text-slate-900">
                <span className="text-sm font-bold">ยอดสุทธิเบื้องต้น:</span>
                <span className="text-xl font-black text-blue-700">
                  ฿{(subtotal + (isFreeShipping ? 0 : distributor.shippingFee)).toLocaleString()}
                </span>
              </div>
            </div>

            <button
              onClick={onProceedToCheckout}
              id="proceed-checkout-button"
              className="w-full py-3 px-4 bg-gradient-to-r from-[#2A59FE] via-[#3B66FE] to-[#4F46E5] hover:opacity-95 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 shadow-md shadow-blue-500/20 transition-all active:scale-[0.98]"
            >
              ดำเนินการสั่งซื้อสินค้า
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
